/**
 * Session hint — a non-secret localStorage flag that mirrors "is there
 * a session cookie present?" without the SPA ever reading the cookie.
 *
 * The str.session cookie is `httpOnly`, so JavaScript cannot see it.
 * The SPA needs *some* signal to render the right Sign in / Sign out
 * affordance, but we deliberately do not call /auth/get-session for
 * this — that endpoint returns the session token in its response body,
 * which would erode the httpOnly cookie protection: an XSS could fetch
 * /auth/get-session and exfiltrate the token. See
 * apps/client/docs/overview.md §"Auth surface" for the full rationale.
 *
 * Instead we keep a non-secret flag in localStorage: written on
 * successful verify-OTP, cleared on sign-out or on any authenticated
 * API request that returns 401. The flag is *purely* a UI cache; the
 * server is still the source of truth and the 401-redirect handler in
 * src/api/client.ts is the single auth side effect the client performs.
 *
 * Persisted in localStorage rather than sessionStorage so the hint
 * survives a browser restart — the cookie does, so the hint should
 * too. The value is not a secret: an attacker who can write to
 * localStorage already controls the origin, and the cookie is out of
 * their reach anyway.
 */

const HINT_KEY = 'str.has_session';

function isStorageAvailable(): boolean {
  // Storage access can throw in two real cases:
  //   1. Non-browser test runtime (`window` is undefined).
  //   2. Browser is in a privacy mode that blocks Storage.
  // Either way the hint is best-effort. Falling back to "no hint" means
  // the UI renders Sign in, which is the safer default — the user can
  // always sign in again.
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

/** Is the session hint currently set? Synchronous read of localStorage. */
export function hasSessionHint(): boolean {
  if (!isStorageAvailable()) return false;
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Record that a sign-in just succeeded. Safe to call repeatedly.
 * Dispatches a synthetic 'storage' event on the *same* tab so listeners
 * subscribed via window.addEventListener('storage', ...) — added for the
 * cross-tab case — also fire locally on the tab that did the write. The
 * native 'storage' event only fires in *other* tabs.
 */
export function setSessionHint(): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(HINT_KEY, '1');
    dispatchLocalStorageEvent('1');
  } catch {
    // Quota exceeded or write blocked — the UI will fall back to the
    // signed-out shell on the next render. Degraded but not broken.
  }
}

/** Record that the session is gone. Safe to call repeatedly. */
export function clearSessionHint(): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(HINT_KEY);
    dispatchLocalStorageEvent(null);
  } catch {
    // Same fallback — best-effort.
  }
}

function dispatchLocalStorageEvent(newValue: string | null): void {
  // The native 'storage' event only fires in *other* tabs, by design.
  // For the same-tab case we synthesize an event so components using a
  // single `useSyncExternalStore` over both same-tab and cross-tab
  // changes don't need separate plumbing.
  try {
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: HINT_KEY,
        newValue,
        storageArea: window.localStorage,
      }),
    );
  } catch {
    // StorageEvent constructor unavailable (very old runtimes). The
    // value is still written; cross-tab subscribers will see it on
    // the next native event. Same-tab consumers that re-render for
    // other reasons (route change, user action) pick it up at that
    // point — degraded but not broken.
  }
}
